import React, { useState, useEffect } from 'react';
import SmsStatusMerke from "./components/SmsStatusMerke";
import { smsVisningsstatus } from "./utils/smsStatus";
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import Cookies from 'js-cookie';
import Ikon from './ny-kundesamhandling/Ikon';
import { Tilbake, Sidetopp, feltKlasse, knappPrimar } from './ny-kundesamhandling/Skjema';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function SendSMS() {
  const location = useLocation();
  const { orderDetails, serviceDetails, customer, templateType, status: initialStatus, hentNummer } = location.state || {};  
  const [customers, setCustomers] = useState([]);
  const [filteredSmsTemplates, setFilteredSmsTemplates] = useState([]);
  const [smsArchive, setSmsArchive] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [highlightedSmsId, setHighlightedSmsId] = useState(null);
  const [smsMessage, setSmsMessage] = useState(null); // State for suksess-/feilmelding
  const [status, setStatus] = useState(initialStatus || 'Avsluttet'); // Bruker status fra location.state hvis den finnes

  const [formData, setFormData] = useState({
    telefonnummer: customer?.phoneNumber || '',
    meldingstekst: '',
    kundeNavn: `${customer?.firstName || ''} ${customer?.lastName || ''}`,
  });

  const selectedStore = Cookies.get('selectedStore') || 'Ukjent butikk';
  const merke = orderDetails?.Varemerke || serviceDetails?.Varemerke || 'Ukjent merke';
  const produkt = orderDetails?.Produkt || serviceDetails?.Produkt || 'Ukjent produkt';
  const id = orderDetails?.ordreid || serviceDetails?.serviceid || 'Ukjent ID';
  const servicetype = serviceDetails?.servicetype || 'Ingen servicetype';
  const hentekode = orderDetails?._id?.slice(-6) || 'Ukjent kode';

  const statusOptions = ['Aktiv', 'Avsluttet', 'Behandles', 'Avventer deler'];

  useEffect(() => {
    // Hent kundeliste og SMS-maler
    const fetchCustomers = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/customers`);
        const data = await response.json();
        setCustomers(data);
      } catch (error) {
        console.error('Feil ved henting av kunder:', error);
      }
    };

    const fetchSmsArchive = async () => {
      try {
        const butikkid = parseInt(Cookies.get('butikkid'), 10) || null;

        // Serveren filtrerer på butikk (butikkid fra cookien).
        const response = await fetch(`${API_BASE_URL}/smsarchives?butikkid=${butikkid || 0}`);
        const data = await response.json();
        const filteredData = data.filter(sms => sms.butikkid === butikkid);

        setSmsArchive(filteredData.reverse());
      } catch (error) {
        console.error('Feil ved henting av SMS-arkiv:', error);
      }
    };


    fetchCustomers();
    fetchSmsArchive();
  }, [orderDetails, serviceDetails, customer, servicetype]);


  // Leveringsstatus: hent arkivet på nytt hvert 5. sekund så lenge en av de
  // siste SMS-ene står som «Sendt» og er under to minutter gammel.
  useEffect(() => {
    const ungSendt = smsArchive.slice(0, 5).some(
      (sms) => smsVisningsstatus(sms).type === 'sendt' && Date.now() - new Date(sms.sendtDato).getTime() < 2 * 60 * 1000
    );
    if (!ungSendt) return undefined;
    const t = setTimeout(async () => {
      try {
        const butikkid = parseInt(Cookies.get('butikkid'), 10) || null;
        const r = await fetch(`${API_BASE_URL}/smsarchives?butikkid=${butikkid || 0}`);
        if (r.ok) setSmsArchive((await r.json()).filter((sms) => sms.butikkid === butikkid).reverse());
      } catch {
        /* prøver igjen */
      }
    }, 5000);
    return () => clearTimeout(t);
  }, [smsArchive]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Sett standard status til 'Avsluttet' hvis templateType er klubbnett, ellers bruk initialStatus eller 'Avsluttet'
  useEffect(() => {
    if (templateType === 'klubbnett' && initialStatus !== 'Avsluttet') {
      setStatus('Avsluttet');
    }
  }, [templateType, initialStatus]);
  
  const handleStatusChange = (e) => {
    setStatus(e.target.value);
  };


  const handleCustomerSelect = (selectedCustomer) => {
    setFormData((prevData) => ({
      ...prevData,
      telefonnummer: selectedCustomer.phoneNumber,
      kundeNavn: `${selectedCustomer.firstName} ${selectedCustomer.lastName}`,
    }));
    setCustomerSearch('');
  };

  useEffect(() => {
    const fetchSmsTemplates = async () => {
      try {
        console.log("🌟 Henter SMS-maler - location.state:", location.state);
        const response = await fetch(`${API_BASE_URL}/smstemplates`);
        const data = await response.json();
        let filteredTemplates = data;

        if (templateType === 'klubbnett') {
          filteredTemplates = data.filter(template => template.type === 'klubbnett');
        } else if (orderDetails) {
          filteredTemplates = data.filter(template => template.type === 'ordre');
        } else if (serviceDetails && servicetype) {
          filteredTemplates = data.filter(template => template.type === servicetype.toLowerCase());
        } else if (serviceDetails) {
          filteredTemplates = data.filter(template => template.type === 'sykkelservice');
        } else if (customer) {
          filteredTemplates = data.filter(template => template.type === 'kunde');
        }

        setFilteredSmsTemplates(filteredTemplates);
      } catch (error) {
        console.error('Feil ved henting av SMS-maler:', error);
      }
    };

    fetchSmsTemplates();
  }, [templateType, orderDetails, serviceDetails, servicetype, customer, location.state]); // ✅ La til location.state


  const handleSmsTemplateChange = (e) => {
    const selectedTemplate = filteredSmsTemplates.find(template => template._id === e.target.value);
    if (selectedTemplate) {
      let updatedMessage = selectedTemplate.tekst
        .replace('%ordreid%', id)
        .replace('%serviceid%', id)
        .replace('%butikk%', selectedStore)
        .replace('%merke%', merke)
        .replace('%produkt%', produkt)
        .replace('%status%', status)
        .replace('%hentNummer%', hentNummer)
        .replace('%hentekode%', hentekode); // 🎯 Legger til hentekoden i meldingen

      setFormData((prevData) => ({
        ...prevData,
        meldingstekst: updatedMessage,
      }));
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    const { telefonnummer, meldingstekst, kundeNavn } = formData;
    try {
      const response = await fetch(`${API_BASE_URL}/sms/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Serveren arkiverer SMS-en (arkiver: true), se POST /sms/send.
        body: JSON.stringify({
          tel: telefonnummer,
          message: meldingstekst,
          arkiver: true,
          kundeNavn,
          butikkid: parseInt(Cookies.get('butikkid'), 10) || 0,
        }),
      });

      if (response.ok) {
        // ✅ Oppdater status til 'Avsluttet' hvis det kommer fra ClubOrderDetails
        if (templateType === 'klubbnett') {
          setStatus('Avsluttet');  // Oppdaterer status i komponenten

          // Oppdater status på serveren også
          const updateUrl = `${API_BASE_URL}/cluborders/${orderDetails._id}/update-status`;
          const body = { status: 'Avsluttet' };

          await fetch(updateUrl, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });

          console.log("✅ Status oppdatert til 'Avsluttet' for klubbordre.");
        } 

        // 🔔 Hvis det er fra ordre eller service, oppdater status basert på state.
        if (orderDetails || serviceDetails) {
          const isOrder = Boolean(orderDetails);
          const updateUrl = isOrder
            ? `${API_BASE_URL}/orders/${orderDetails._id}`
            : `${API_BASE_URL}/services/${serviceDetails._id}`;
        
          const avsluttetDato = new Date().toISOString();
        
          const body = isOrder
          ? { Status: status }
          : status?.toLowerCase() === 'avsluttet'
            ? { status: status, avsluttetDato }
            : { status: status };
        
        
          await fetch(updateUrl, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });
        
          if (!isOrder && status?.toLowerCase() === 'avsluttet') {
            console.log(`📅 Service avsluttet ${avsluttetDato} for ID: ${id}`);
          }
          
        
          console.log("✅ Status oppdatert på serveren.");
        }
        
        

        setSmsMessage('Status oppdatert og SMS sendt.'); // Oppdater state med suksessmelding

 // 🔥 Oppdater SMS-arkivet i komponenten og highlight nyeste SMS
 const updatedArchive = await fetch(`${API_BASE_URL}/smsarchives?butikkid=${parseInt(Cookies.get('butikkid'), 10) || 0}`);
 const updatedArchiveData = await updatedArchive.json();
 setSmsArchive(updatedArchiveData.reverse());

 // ✅ Marker nyeste SMS med grønn ramme
 const newSmsId = updatedArchiveData[0]._id;
 setHighlightedSmsId(newSmsId);
 setTimeout(() => setHighlightedSmsId(null), 5000); // Fjern markeringen etter 5 sekunder


        // Nullstill skjemaet etter 2 sekunder
        setTimeout(() => setSmsMessage(null), 2000);
        setFormData({ telefonnummer: '', meldingstekst: '', kundeNavn: '' });

      } else if (response.status === 429) {
        // Taket per mottaker (src/lib/sms.js): serveren forklarer hvorfor.
        const data = await response.json().catch(() => ({}));
        setSmsMessage(data.message || "Kunden har fått mange SMS fra oss nylig, så denne ble ikke sendt.");
      } else {
        console.error("Feil ved sending av SMS:", response.status);
        setSmsMessage("Kunne ikke sende SMS. Vennligst sjekk innstillingene dine.");
      }
    } catch (error) {
      console.error('Feil ved kommunikasjon med serveren:', error);
      setSmsMessage("En feil oppstod ved sending av SMS.");
    }
  };



  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredSmsArchive = smsArchive.filter((sms) =>
    sms.kundeNavn.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sms.telefonnummer.includes(searchTerm) ||
    sms.meldingstekst.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCustomers =
    customerSearch.length >= 2
      ? customers.filter((customer) =>
        `${customer.firstName} ${customer.lastName} ${customer.phoneNumber}`
          .toLowerCase()
          .includes(customerSearch.toLowerCase())
      )
      : [];

  const lastTenSms = filteredSmsArchive.slice(0, 5);

  const formatDateTime = (dateString) => {
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm');
    } catch (error) {
      return 'Ukjent dato';
    }
  };

  // Utseende i det nye designet (vinnsel, 30.10). Skjemaet og handlingene er de samme.
  const smsOk = smsMessage === 'Status oppdatert og SMS sendt.';

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      <Tilbake />
      <Sidetopp ikon="send" farge="kunde" tittel="Send SMS" undertittel="Til en kunde, med eller uten SMS-mal" />

      {smsMessage && (
        <p
          role={smsOk ? 'status' : 'alert'}
          className={`rounded-xl border px-3 py-2 font-medium ${smsOk ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-red-200 bg-red-50 text-red-800'}`}
        >
          {smsMessage}
        </p>
      )}

      <form onSubmit={handleSubmit} className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
        <div className="grid gap-3 sm:grid-cols-2">
          {/* Kundesøk */}
          <div className="relative min-w-0">
            <label htmlFor="sms-kundesok" className="mb-1 block font-semibold text-slate-800">Søk etter kunde (navn eller telefonnummer)</label>
            <input
              id="sms-kundesok"
              type="search"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              className={feltKlasse}
              placeholder="Skriv inn navn eller telefonnummer"
              autoComplete="off"
            />
            {filteredCustomers.length > 0 && (
              <ul className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl">
                {filteredCustomers.map((customer) => (
                  <li key={customer._id}>
                    <button
                      type="button"
                      onClick={() => handleCustomerSelect(customer)}
                      className="w-full px-3 py-1.5 text-left hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none"
                    >
                      <span className="font-semibold text-slate-900">{customer.firstName} {customer.lastName}</span>
                      <span className="font-mono text-slate-600"> · {customer.phoneNumber}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="min-w-0">
            <label htmlFor="sms-telefon" className="mb-1 block font-semibold text-slate-800">
              Telefonnummer<span aria-hidden="true" className="text-red-700"> *</span><span className="sr-only"> (må fylles ut)</span>
            </label>
            <input
              id="sms-telefon"
              type="tel"
              name="telefonnummer"
              value={formData.telefonnummer}
              onChange={handleChange}
              required
              className={`${feltKlasse} font-mono`}
              placeholder="Skriv inn telefonnummer"
            />
          </div>
        </div>

        {(templateType === 'klubbnett' || orderDetails || serviceDetails) && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="min-w-0">
              <label htmlFor="sms-mal" className="mb-1 block font-semibold text-slate-800">Velg SMS-mal</label>
              <select id="sms-mal" onChange={handleSmsTemplateChange} className={feltKlasse}>
                <option value="">Velg en SMS-mal</option>
                {filteredSmsTemplates.map((template) => (
                  <option key={template._id} value={template._id}>
                    {template.tittel}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-0">
              <label htmlFor="sms-status" className="mb-1 block font-semibold text-slate-800">Oppdater status</label>
              <select id="sms-status" value={status} onChange={handleStatusChange} className={feltKlasse}>
                {statusOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div>
          <label htmlFor="sms-tekst" className="mb-1 block font-semibold text-slate-800">
            Meldingstekst<span aria-hidden="true" className="text-red-700"> *</span><span className="sr-only"> (må fylles ut)</span>
          </label>
          <textarea
            id="sms-tekst"
            name="meldingstekst"
            rows={4}
            value={formData.meldingstekst}
            onChange={handleChange}
            required
            className={feltKlasse}
            placeholder="Skriv inn meldingsteksten"
          />
          {id !== 'Ukjent ID' && (
            <p className="mt-1 text-sm text-slate-600">
              {servicetype === 'Ingen servicetype' ? 'Ordre' : servicetype} – Merke: {merke}, Produkt: {produkt}, ID: {id}, Status endres til: {status}
            </p>
          )}
        </div>

        <button type="submit" className={knappPrimar}>
          <Ikon navn="send" className="w-5 h-5" /> Send SMS
        </button>
      </form>

      {/* Siste sendte */}
      <section aria-labelledby="sms-siste" className="space-y-2">
        <div className="flex flex-wrap items-end gap-3">
          <h2 id="sms-siste" className="text-lg font-bold text-slate-900">Siste sendte SMS-er</h2>
          <div className="ml-auto w-full sm:w-96">
            <label htmlFor="sms-sok" className="sr-only">Søk etter SMS</label>
            <input
              id="sms-sok"
              type="search"
              placeholder="Søk etter SMS (kunde, telefonnummer, melding)"
              value={searchTerm}
              onChange={handleSearchChange}
              className={feltKlasse}
            />
          </div>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="min-w-full text-left">
            <thead className="bg-slate-100 text-sm font-semibold text-slate-700">
              <tr>
                <th scope="col" className="px-3 py-1.5">Kunde</th>
                <th scope="col" className="px-3 py-1.5">Telefonnummer</th>
                <th scope="col" className="px-3 py-1.5">Status</th>
                <th scope="col" className="hidden px-3 py-1.5 md:table-cell">Meldingstekst</th>
                <th scope="col" className="hidden px-3 py-1.5 md:table-cell">Sendt dato</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 text-sm text-slate-800">
              {lastTenSms.map((sms) => (
                <tr
                  key={sms._id}
                  className={`align-top hover:bg-slate-50 ${sms._id === highlightedSmsId ? 'outline outline-2 -outline-offset-2 outline-emerald-500' : ''}`}
                >
                  <td className="px-3 py-1.5 font-medium">{sms.kundeNavn || 'Ukjent'}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono">{sms.telefonnummer}</td>
                  <td className="px-3 py-1.5"><SmsStatusMerke sms={sms} visGrunn /></td>
                  <td className="hidden px-3 py-1.5 md:table-cell [overflow-wrap:anywhere]">{sms.meldingstekst}</td>
                  <td className="hidden whitespace-nowrap px-3 py-1.5 md:table-cell">{formatDateTime(sms.sendtDato)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default SendSMS;
